import { cn } from '@/lib/utils'
import { Media } from '@/components/Media'
import { AvatarUser } from '@/components/AvatarUser'
import { AuctionCountdown } from '@/components/AuctionCountdown'
import { TestLabelButton } from '@/components/dashboard/TestLabelButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { authStore } from '@/lib/stores/auth'
import { notificationActions, notificationStore } from '@/lib/stores/notifications'
import { uiActions } from '@/lib/stores/ui'
import { usePublishAuctionSettlementMutation } from '@/publish/auctions'
import {
	auctionsByPubkeyQueryOptions,
	getAuctionBiddingCutoffAt,
	getAuctionBidCountFromBids,
	getAuctionId,
	getAuctionImages,
	getAuctionRootEventId,
	getAuctionSettlementGrace,
	getAuctionStartAt,
	getAuctionSummary,
	getAuctionTitle,
	getAuctionTopBidFromBids,
	getBidAmount,
	useAuctionBidsForList,
	useAuctionClaimOrders,
	useAuctionPathReleasesForList,
	useAuctionSettlementsForList,
} from '@/queries/auctions'
import { useComments } from '@/queries/comments'
import { useLiveActivity, useLiveChatMessages } from '@/queries/liveChat'
import { AUCTION_KIND } from '@/lib/auction/constants'
import { getOrderId } from '@/queries/orders'
import { useProfileName } from '@/queries/profiles'
import type { NDKEvent } from '@/lib/nostr/ndk-events'
import { useDashboardTitle } from '@/routes/_dashboard-layout'
import { parseSettlementEvent } from '@/lib/schemas/auction/settlementEvents'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet, useMatchRoute } from '@tanstack/react-router'
import { useSelector } from '@tanstack/react-store'
import { CheckCheck, Clock, ExternalLink, Gavel, Hourglass, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { auctionSortOptionValues, getAuctionSortOptionTitle, useFilteredAuctions, type AuctionSortOption } from '@/lib/utils/auctions'

type AuctionStatus = 'Scheduled' | 'Live' | 'Settlement' | 'Ended'

function formatAuctionStatus(startAt: number, biddingCutoffAt: number, settlementLocked: boolean, now: number): AuctionStatus {
	if (startAt > 0 && now < startAt) return 'Scheduled'
	if (settlementLocked) return 'Ended'
	if (biddingCutoffAt > 0 && now >= biddingCutoffAt) return 'Settlement'
	return 'Live'
}

function formatMaybeDate(timestamp: number): string {
	if (!timestamp) return 'N/A'
	return new Date(timestamp * 1000).toLocaleString('en-US', {
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		month: 'long',
		year: 'numeric',
	})
}

function formatTimeAgo(timestamp: number): string {
	if (!timestamp) return ''
	const diffSeconds = Math.max(0, Math.floor(Date.now() / 1000) - timestamp)
	if (diffSeconds < 60) return 'just now'
	const diffMinutes = Math.floor(diffSeconds / 60)
	if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`
	const diffHours = Math.floor(diffMinutes / 60)
	if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
	const diffDays = Math.floor(diffHours / 24)
	return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
}

function formatTimeLeft(seconds: number): string {
	if (seconds <= 0) return 'Expired'
	const days = Math.floor(seconds / 86400)
	if (days > 0) return `${days} day${days === 1 ? '' : 's'}`
	const hours = Math.floor(seconds / 3600)
	if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'}`
	const minutes = Math.floor(seconds / 60)
	if (minutes > 0) return `${minutes} min${minutes === 1 ? '' : 's'}`
	return `${seconds} sec${seconds === 1 ? '' : 's'}`
}

const getAuctionCoordinates = (auction: NDKEvent): string => {
	const auctionDTag = getAuctionId(auction)
	return auctionDTag ? `30408:${auction.pubkey}:${auctionDTag}` : ''
}

const compareEventRecencyDescending = (left: NDKEvent, right: NDKEvent): number => {
	const createdAtDelta = (right.created_at || 0) - (left.created_at || 0)
	if (createdAtDelta !== 0) return createdAtDelta
	return right.id.localeCompare(left.id)
}

const dedupeEventsById = (events: NDKEvent[]): NDKEvent[] => {
	const byId = new Map<string, NDKEvent>()
	for (const event of events) {
		if (!event?.id) continue
		byId.set(event.id, event)
	}
	return Array.from(byId.values())
}

const isCanonicalSellerSettlementForAuction = (
	event: NDKEvent,
	auction: NDKEvent,
	auctionRootEventId: string,
	auctionCoordinates: string,
): boolean => {
	const parsed = parseSettlementEvent(event)
	if (!parsed.ok) return false

	const authorMatchesSeller = event.pubkey === auction.pubkey || parsed.value.sellerPubkey === auction.pubkey
	if (!authorMatchesSeller) return false

	const rootReferenceMatches = parsed.value.auctionRootEventId === auctionRootEventId
	const coordinateReferenceMatches = parsed.value.auctionCoordinate === auctionCoordinates
	return rootReferenceMatches || coordinateReferenceMatches
}

const resolveLatestCanonicalSettlementForAuction = (
	auction: NDKEvent,
	auctionRootEventId: string,
	auctionCoordinates: string,
	settlementsByAuctionKey?: Map<string, NDKEvent[]>,
): NDKEvent | null => {
	const events = dedupeEventsById([
		...(settlementsByAuctionKey?.get(auctionRootEventId) ?? []),
		...(settlementsByAuctionKey?.get(auctionCoordinates) ?? []),
	])

	const matching = events.filter((event) => isCanonicalSellerSettlementForAuction(event, auction, auctionRootEventId, auctionCoordinates))
	if (matching.length === 0) return null

	return [...matching].sort(compareEventRecencyDescending)[0] ?? null
}

const STATUS_BADGE_STYLES: Record<AuctionStatus, string> = {
	Scheduled: 'border-sky-400 text-sky-700 bg-sky-50',
	Live: 'border-emerald-400 text-emerald-700 bg-emerald-50',
	Settlement: 'border-sky-400 text-sky-700 bg-sky-50',
	Ended: 'border-zinc-300 text-zinc-500 bg-zinc-50',
}

function ActivityRow({ header, unit, count, newCount }: { header: string; unit: string; count: number; newCount: number }) {
	return (
		<div className="space-y-0.5">
			<p className="text-xs text-muted-foreground">{header}</p>
			<p className="text-sm">
				<span className="font-semibold">
					{count} {count === 1 ? unit : `${unit}s`}
				</span>
				{newCount > 0 && <span className="ml-2 text-pink-600 font-semibold">{newCount} New</span>}
			</p>
		</div>
	)
}

function TopBidBox({
	auction,
	bids,
	className,
	isSettlementPhase,
	showReleasePathDetails,
	showWaitingForReleasePath,
	showReleasePathReceived,
	showSettlementGrieved,
	showSettlementGrievedNoRelease,
	showWaitingForShippingMethod,
	settlementSecondsLeft,
	settlementDeadlineAt,
}: {
	auction: NDKEvent
	bids: NDKEvent[]
	className?: string
	isSettlementPhase: boolean
	showReleasePathDetails: boolean
	showWaitingForReleasePath: boolean
	showReleasePathReceived: boolean
	showSettlementGrieved: boolean
	showSettlementGrievedNoRelease: boolean
	showWaitingForShippingMethod: boolean
	settlementSecondsLeft: number
	settlementDeadlineAt: number
}) {
	const topBid = getAuctionTopBidFromBids(auction, bids)
	const { data: bidderName } = useProfileName(topBid?.pubkey ?? '')

	if (!topBid) {
		return (
			<div
				className={cn(
					'flex h-full min-h-32 items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-zinc-50 px-5 py-4',
					className,
				)}
			>
				<p className="text-center text-sm text-zinc-500">No bids yet. Winning bid will appear here.</p>
			</div>
		)
	}

	return (
		<div
			className={cn(
				'h-full min-h-32 rounded-xl border-2 px-4 py-4',
				isSettlementPhase ? 'border-emerald-300 bg-emerald-100' : 'border-zinc-300 bg-zinc-100',
				className,
			)}
		>
			<div>
				<div
					className={cn(
						'flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em]',
						isSettlementPhase ? 'text-emerald-800' : 'text-zinc-700',
					)}
				>
					<span>Top bid:</span>
					<span>{formatTimeAgo(topBid.created_at ?? 0)}</span>
				</div>
				<p className="mt-1 text-2xl font-semibold tracking-tight text-zinc-950">{getBidAmount(topBid).toLocaleString()} sats</p>
			</div>
			<div className="mt-2 flex items-center justify-between gap-2">
				<Badge
					className={cn(
						'bg-white hover:bg-white',
						isSettlementPhase ? 'border-emerald-300 text-emerald-800' : 'border-zinc-300 text-zinc-700',
					)}
				>
					{isSettlementPhase ? 'Winning bid' : 'Current top bid'}
				</Badge>

				<div className="flex items-center gap-2">
					<Link to="/profile/$profileId" params={{ profileId: topBid.pubkey }}>
						<AvatarUser pubkey={topBid.pubkey} colored deterministicFallbackText className="w-6 h-6" />
					</Link>
					<span className="text-sm font-medium truncate max-w-32">{bidderName || topBid.pubkey.slice(0, 8)}</span>
				</div>
			</div>
			{showReleasePathDetails && (
				<Accordion type="single" collapsible className="mt-3 rounded-xl border border-zinc-200 bg-white px-4">
					<AccordionItem value={`release-details-${topBid.id}`} className="border-none">
						<AccordionTrigger className="py-4 text-sm font-semibold text-zinc-900 hover:no-underline">Settlement details</AccordionTrigger>
						<AccordionContent className="space-y-3 pb-4">
							{showWaitingForReleasePath && (
								<div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
									<div className="font-medium">Waiting for release path</div>
									<div className="mt-1">Bidder must publish the release path before settlement can be published.</div>
								</div>
							)}
							{showReleasePathReceived && (
								<div className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900">
									<div className="font-medium">Release path received</div>
									<div className="mt-1">{formatTimeLeft(settlementSecondsLeft)} to publish settlement</div>
								</div>
							)}
							{showSettlementGrieved && (
								<div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900">
									Bid was not settled. It was grieved after release path.
								</div>
							)}
							{showSettlementGrievedNoRelease && (
								<div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900">
									Bid was not settled. It was grieved because release path was not published.
								</div>
							)}
							{showWaitingForShippingMethod && (
								<Button
									type="button"
									variant="outline"
									disabled
									className="w-full justify-start gap-2 border-sky-300 bg-sky-50 text-sky-900 hover:bg-sky-50"
								>
									<Hourglass className="h-4 w-4" aria-hidden="true" />
									Waiting for bidder shipping method
								</Button>
							)}
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			)}
		</div>
	)
}

function AuctionListItem({
	auction,
	bids,
	latestSettlement,
	pathReleases,
	onPublishSettlement,
	isSettling,
}: {
	auction: NDKEvent
	bids: NDKEvent[]
	latestSettlement: NDKEvent | null
	pathReleases: NDKEvent[]
	onPublishSettlement: () => void
	isSettling: boolean
}) {
	const summary = getAuctionSummary(auction) || auction.content || 'No description'
	const images = getAuctionImages(auction)
	const thumbnailUrl = images.length > 0 ? images[0][1] : null
	const startAt = getAuctionStartAt(auction)
	const auctionNotificationKey =
		getAuctionRootEventId(auction) || (getAuctionId(auction) ? `30408:${auction.pubkey}:${getAuctionId(auction)}` : auction.id)
	const auctionCoordinates = getAuctionCoordinates(auction)
	const biddingCutoffAt = getAuctionBiddingCutoffAt(auction)
	const now = Math.floor(Date.now() / 1000)
	const {
		lastSeenTimestamps: {
			auctionBids: lastSeenAuctionBidsGlobal,
			auctionBidsByAuction,
			auctionComments: lastSeenAuctionCommentsGlobal,
			auctionCommentsByAuction,
			auctionEventComments: lastSeenAuctionEventCommentsGlobal,
			auctionEventCommentsByAuction,
			auctionLive: lastSeenAuctionLiveGlobal,
			auctionLiveByAuction,
			auctionSettlementBegins: lastSeenAuctionSettlementBeginsGlobal,
			auctionSettlementBeginsByAuction,
		},
	} = useSelector(notificationStore)
	const lastSeenAuctionBids = Math.max(lastSeenAuctionBidsGlobal, auctionBidsByAuction[auctionNotificationKey] || 0)
	const lastSeenAuctionComments = Math.max(lastSeenAuctionCommentsGlobal, auctionCommentsByAuction[auctionNotificationKey] || 0)
	const lastSeenAuctionEventComments = Math.max(
		lastSeenAuctionEventCommentsGlobal,
		auctionEventCommentsByAuction[auctionNotificationKey] || 0,
	)
	const lastSeenAuctionLive = Math.max(lastSeenAuctionLiveGlobal, auctionLiveByAuction[auctionNotificationKey] || 0)
	const lastSeenAuctionSettlementBegins = Math.max(
		lastSeenAuctionSettlementBeginsGlobal,
		auctionSettlementBeginsByAuction[auctionNotificationKey] || 0,
	)

	const bidsCount = getAuctionBidCountFromBids(auction, bids)
	const topBid = getAuctionTopBidFromBids(auction, bids)
	const newBidsCount = bids.filter((bid) => (bid.created_at ?? 0) > lastSeenAuctionBids).length

	const settlementLocked = !!latestSettlement

	const status = formatAuctionStatus(startAt, biddingCutoffAt, settlementLocked, now)
	const hasBids = bidsCount > 0
	const badgeStatus: AuctionStatus = status === 'Settlement' && !hasBids ? 'Ended' : status
	const isSettlementPhase = (status === 'Settlement' || status === 'Ended') && hasBids
	const settlementGraceSeconds = getAuctionSettlementGrace(auction)
	const settlementDeadlineAt = biddingCutoffAt > 0 ? biddingCutoffAt + settlementGraceSeconds : 0
	const settlementSecondsLeft = settlementDeadlineAt > 0 ? Math.max(0, settlementDeadlineAt - now) : 0
	const settlementGraceExpired = settlementDeadlineAt > 0 && now >= settlementDeadlineAt && !latestSettlement
	const winnerReleasedPath = !!(
		topBid && pathReleases.some((pathRelease) => pathRelease.tags.find((tag) => tag[0] === 'e')?.[1] === topBid.id)
	)
	const waitingForReleasePath = status === 'Settlement' && hasBids && !!topBid && !winnerReleasedPath && !settlementGraceExpired
	const releasePathReceived = status === 'Settlement' && hasBids && winnerReleasedPath && !settlementGraceExpired
	const settlementGrieved = status === 'Settlement' && hasBids && settlementGraceExpired && !latestSettlement && winnerReleasedPath
	const settlementGrievedNoRelease =
		status === 'Settlement' && hasBids && settlementGraceExpired && !latestSettlement && !winnerReleasedPath
	const canPublishSettlementNow = status === 'Settlement' && hasBids && !settlementGraceExpired && (!topBid || winnerReleasedPath)

	const commentsQuery = useComments(auction)
	const comments = commentsQuery.data ?? []
	const newCommentsCount = comments.filter((comment) => comment.createdAt > lastSeenAuctionEventComments).length
	const liveActivityQuery = useLiveActivity(auction)
	const liveActivityCoord = liveActivityQuery.data?.coord ?? ''
	const chatQuery = useLiveChatMessages(liveActivityCoord, status === 'Live')
	const chatMessages = chatQuery.data ?? []
	const newChatCount = chatMessages.filter((message) => message.createdAt > lastSeenAuctionComments).length
	const hasNewLiveStatus = startAt > 0 && startAt <= now && startAt > lastSeenAuctionLive
	const hasNewSettlementStatus = biddingCutoffAt > 0 && biddingCutoffAt <= now && biddingCutoffAt > lastSeenAuctionSettlementBegins
	const hasNewActivity = newBidsCount + newCommentsCount + newChatCount > 0 || hasNewLiveStatus || hasNewSettlementStatus

	const handleMarkActivitySeen = () => {
		notificationActions.markAuctionBidsSeen(auctionNotificationKey)
		notificationActions.markAuctionCommentsSeen(auctionNotificationKey)
		notificationActions.markAuctionEventCommentsSeen(auctionNotificationKey)
		notificationActions.markAuctionLiveSeen(auctionNotificationKey)
		notificationActions.markAuctionSettlementBeginsSeen(auctionNotificationKey)
	}

	const claimOrdersQuery = useAuctionClaimOrders(auctionCoordinates)
	const orderId = claimOrdersQuery.data?.[0] ? getOrderId(claimOrdersQuery.data[0]) : undefined
	const waitingForShippingMethod = status === 'Ended' && !!latestSettlement && winnerReleasedPath && !orderId
	const showReleasePathDetails =
		waitingForReleasePath || releasePathReceived || settlementGrieved || settlementGrievedNoRelease || waitingForShippingMethod

	return (
		<div className="rounded-lg border border-zinc-200 bg-background p-6 shadow-md">
			<div className="flex flex-col gap-6 lg:flex-row">
				<div className="flex shrink-0 items-center gap-3 lg:flex-col lg:items-center">
					{thumbnailUrl ? (
						<div className="overflow-hidden rounded-xl border border-white/60 bg-zinc-100 p-1 shadow-sm ring-1 ring-zinc-200/70">
							<Media src={thumbnailUrl} alt="" className="h-24 w-24 shrink-0 object-cover lg:h-32 lg:w-32" />
						</div>
					) : (
						<div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-zinc-50 shadow-sm ring-1 ring-zinc-200/70 lg:h-32 lg:w-32">
							<Gavel className="h-8 w-8 text-zinc-400" />
						</div>
					)}
					<Link to="/auctions/$auctionId" params={{ auctionId: auction.id }} className="w-24 lg:w-32">
						<Button
							size="sm"
							className="w-full gap-2 rounded-xl border border-zinc-300 bg-neutral-800 text-white shadow-sm hover:bg-neutral-700"
						>
							<ExternalLink className="h-3.5 w-3.5" />
							Open Auction
						</Button>
					</Link>
					{/* ADR-0009: authorized labelers can mark / unmark this auction as a test listing */}
					<TestLabelButton
						kind={AUCTION_KIND}
						pubkey={auction.pubkey}
						dTag={getAuctionId(auction)}
						itemLabel="Auction"
						className="w-full lg:w-32"
					/>
				</div>

				<div className="flex min-w-0 flex-1 flex-col gap-4 lg:max-w-[28rem]">
					<div className="min-w-0">
						<h3 className="text-lg font-semibold text-foreground truncate">{getAuctionTitle(auction)}</h3>
						<p className="text-sm text-muted-foreground truncate">{summary}</p>
						<p className="mt-1 text-xs text-muted-foreground">Created: {formatMaybeDate(auction.created_at ?? 0)}</p>
					</div>

					<TopBidBox
						auction={auction}
						bids={bids}
						className="flex-1"
						isSettlementPhase={isSettlementPhase}
						showReleasePathDetails={showReleasePathDetails}
						showWaitingForReleasePath={waitingForReleasePath}
						showReleasePathReceived={releasePathReceived}
						showSettlementGrieved={settlementGrieved}
						showSettlementGrievedNoRelease={settlementGrievedNoRelease}
						showWaitingForShippingMethod={waitingForShippingMethod}
						settlementSecondsLeft={settlementSecondsLeft}
						settlementDeadlineAt={settlementDeadlineAt}
					/>

					{status !== 'Ended' && hasBids && (
						<Button
							className="mt-auto w-full bg-neutral-800 text-white hover:bg-neutral-700 disabled:border disabled:border-zinc-300 disabled:bg-zinc-200 disabled:text-zinc-500"
							onClick={onPublishSettlement}
							disabled={!canPublishSettlementNow || isSettling}
						>
							{isSettling ? (
								<>
									<Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
									Publishing…
								</>
							) : (
								'Publish Settlement'
							)}
						</Button>
					)}

					{status === 'Ended' && orderId && (
						<Link to="/dashboard/orders/$orderId" params={{ orderId }} className="mt-auto w-full">
							<Button className="w-full bg-neutral-800 hover:bg-neutral-700 text-white">Go to Order Page</Button>
						</Link>
					)}

					{waitingForShippingMethod && (
						<Button
							type="button"
							variant="outline"
							disabled
							className="mt-2 w-full justify-start gap-2 border-sky-300 bg-sky-50 text-sky-900 hover:bg-sky-50"
						>
							<Hourglass className="h-4 w-4" aria-hidden="true" />
							Waiting for shipping method
						</Button>
					)}
				</div>

				<div className="ml-auto flex w-full shrink-0 flex-col items-end gap-3 lg:w-[22rem]">
					<div className="flex w-full items-center gap-2">
						<div className="min-w-0 flex-1">
							<AuctionCountdown auction={auction} bids={bids} />
						</div>
						<span
							className={cn(
								'inline-flex w-fit whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium',
								STATUS_BADGE_STYLES[badgeStatus],
							)}
						>
							{badgeStatus}
						</span>
					</div>
					<div className="w-full space-y-3 rounded-xl border border-zinc-200 bg-zinc-50 px-5 py-5">
						<div className="flex items-center justify-between gap-3">
							<p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Activity</p>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={handleMarkActivitySeen}
								disabled={!hasNewActivity}
								className="h-7 rounded-md border border-zinc-300 bg-white px-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground hover:bg-zinc-100 hover:text-foreground"
							>
								<CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
								Mark as seen
							</Button>
						</div>
						<ActivityRow header="Bids" unit="Bid" count={bidsCount} newCount={newBidsCount} />
						<ActivityRow header="Comments" unit="Comment" count={comments.length} newCount={newCommentsCount} />
						<ActivityRow header="Live Chat" unit="Message" count={chatMessages.length} newCount={newChatCount} />
					</div>
				</div>
			</div>
		</div>
	)
}

export const Route = createFileRoute('/_dashboard-layout/dashboard/products/auctions')({
	component: AuctionsOverviewComponent,
})

function AuctionsOverviewComponent() {
	const { user, isAuthenticated } = useSelector(authStore)
	const matchRoute = useMatchRoute()
	const [sort, setSort] = useState<AuctionSortOption>('newest')
	const settlementMutation = usePublishAuctionSettlementMutation()
	const [settlingAuctionId, setSettlingAuctionId] = useState<string | null>(null)
	const [animationParent] = useAutoAnimate()

	const isOnChildRoute = matchRoute({
		to: '/dashboard/products/auctions/$auctionId',
		fuzzy: true,
	})

	useDashboardTitle(isOnChildRoute ? 'Auction Details' : 'Auctions')

	const {
		data: auctions,
		isLoading,
		error,
	} = useQuery({
		// Owner surface: this list is the seller's own inventory, so it keeps
		// malformed events visible (`includeInvalid`) while the public browse
		// surfaces drop them. A seller has to see the event to republish a
		// corrected version; the detail page carries the notice that says why.
		...auctionsByPubkeyQueryOptions(user?.pubkey ?? '', 100, { includeInvalid: true }),
		enabled: !!user?.pubkey && isAuthenticated,
	})

	const auctionRootEventIdsForBids = useMemo(
		() => (auctions ?? []).map((auction) => getAuctionRootEventId(auction) || auction.id),
		[auctions],
	)
	const auctionCoordinatesForList = useMemo(() => (auctions ?? []).map((auction) => getAuctionCoordinates(auction)), [auctions])
	const { data: bidsByAuctionId } = useAuctionBidsForList(auctionRootEventIdsForBids)
	const { data: settlementsByAuctionKey } = useAuctionSettlementsForList(auctionRootEventIdsForBids, auctionCoordinatesForList, 5)
	const { data: pathReleasesByAuctionCoordinate } = useAuctionPathReleasesForList(auctionCoordinatesForList, 200)
	const sortedAuctions = useFilteredAuctions({ auctions: auctions ?? [], filters: { sort: sort }, bidsByAuctionId, tag: undefined })

	const handlePublishSettlement = async (auction: NDKEvent) => {
		const auctionRootEventId = getAuctionRootEventId(auction)
		const auctionCoordinates = getAuctionCoordinates(auction)
		setSettlingAuctionId(auction.id)
		try {
			// Backend computes the actual outcome (settled / reserve_not_met)
			// from bids + reserve; we don't pre-pick a status here.
			await settlementMutation.mutateAsync({
				auctionEventId: auctionRootEventId || auction.id,
				auctionCoordinates,
			})
		} catch {
			// Toast handled by mutation hook.
		} finally {
			setSettlingAuctionId(null)
		}
	}

	const handleCreateAuction = () => {
		uiActions.openDrawer('createAuction')
	}

	if (!isAuthenticated || !user) {
		return (
			<div className="p-6 text-center">
				<p>Please log in to manage your auctions.</p>
			</div>
		)
	}

	if (isOnChildRoute) {
		return <Outlet />
	}

	return (
		<div>
			<div className="hidden lg:flex sticky top-0 z-10 bg-white border-b py-4 px-4 lg:px-6 items-center justify-between">
				<h1 className="text-2xl font-bold">Auctions</h1>
				<div className="flex items-center gap-4">
					<Select value={sort} onValueChange={(value) => setSort(value as AuctionSortOption)}>
						<SelectTrigger className="w-56">
							<SelectValue placeholder="Order By" />
						</SelectTrigger>
						<SelectContent>
							{auctionSortOptionValues.map((value) => (
								<SelectItem key={value} value={value}>
									{getAuctionSortOptionTitle(value)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Link to="/auctions">
						<Button variant="outline" className="gap-2">
							<ExternalLink className="w-4 h-4" />
							View Public Auctions
						</Button>
					</Link>
					<Button
						onClick={handleCreateAuction}
						className="bg-neutral-800 hover:bg-neutral-700 text-white flex items-center gap-2 px-4 py-2 text-sm font-semibold"
					>
						<Gavel className="w-4 h-4" />
						Add An Auction
					</Button>
				</div>
			</div>

			<div className="space-y-6 p-4 lg:p-6">
				<div className="lg:hidden space-y-4">
					<Select value={sort} onValueChange={(value) => setSort(value as AuctionSortOption)}>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Order By" />
						</SelectTrigger>
						<SelectContent>
							{auctionSortOptionValues.map((value) => (
								<SelectItem key={value} value={value}>
									{getAuctionSortOptionTitle(value)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Button
						onClick={handleCreateAuction}
						data-testid="add-auction-button-mobile"
						className="w-full bg-neutral-800 hover:bg-neutral-700 text-white flex items-center justify-center gap-2 py-3 text-base font-semibold rounded-t-md rounded-b-none border-b border-neutral-600"
					>
						<Gavel className="w-4 h-4" /> Add An Auction
					</Button>
				</div>

				<div>
					{isLoading && (
						<div className="text-center py-8 text-gray-500">
							<Clock className="animate-spin h-6 w-6 mx-auto mb-2" />
							Loading auctions...
						</div>
					)}

					{error && (
						<div className="text-center py-8 text-red-500">
							Failed to load your auctions: {error instanceof Error ? error.message : 'Unknown error'}
						</div>
					)}

					{!isLoading && !error && sortedAuctions.length === 0 && (
						<div className="text-center py-12 border rounded-lg">
							<Gavel className="h-10 w-10 mx-auto mb-3 text-gray-400" />
							<h3 className="text-lg font-medium mb-1">No auctions yet</h3>
							<p className="text-muted-foreground mb-4">Click the "Add An Auction" button to create your first one.</p>
							<Button onClick={handleCreateAuction} className="bg-neutral-800 hover:bg-neutral-700 text-white">
								Add An Auction
							</Button>
						</div>
					)}

					{!isLoading && !error && sortedAuctions.length > 0 && (
						<ul ref={animationParent} className="flex flex-col gap-4 mt-4">
							{sortedAuctions.map((auction) => (
								<li key={auction.id}>
									{(() => {
										const auctionRootEventId = getAuctionRootEventId(auction) || auction.id
										const auctionCoordinates = getAuctionCoordinates(auction)
										const bids = bidsByAuctionId?.get(auctionRootEventId) ?? []
										const latestSettlement = resolveLatestCanonicalSettlementForAuction(
											auction,
											auctionRootEventId,
											auctionCoordinates,
											settlementsByAuctionKey,
										)
										const pathReleases = pathReleasesByAuctionCoordinate?.get(auctionCoordinates) ?? []

										return (
											<AuctionListItem
												auction={auction}
												bids={bids}
												latestSettlement={latestSettlement}
												pathReleases={pathReleases}
												onPublishSettlement={() => void handlePublishSettlement(auction)}
												isSettling={settlingAuctionId === auction.id && settlementMutation.isPending}
											/>
										)
									})()}
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>
	)
}
